// =========================================================
// Vojjin DATETIME Utilities
// =========================================================
import {registerGlobal} from "./core.js";

class Datetime {

    static date = null;
    static locale = 'en-US';
    static useUTC = false;

    static #_get(name, methodUTC, methodLocal) {
        return this.useUTC
            ? this.date[`getUTC${methodUTC}`]()
            : this.date[`get${methodLocal}`]();
    }

    static #_pad(n, len = 2) {
        return String(n).padStart(len, '0');
    }

    static #_getMonthName(short = false) {
        return new Intl.DateTimeFormat(this.locale, {
            month: short ? 'short' : 'long',
            timeZone: this.useUTC ? 'UTC' : undefined
        }).format(this.date);
    }

    static #_getWeekdayName(short = false) {
        return new Intl.DateTimeFormat(this.locale, {
            weekday: short ? 'short' : 'long',
            timeZone: this.useUTC ? 'UTC' : undefined
        }).format(this.date);
    }

    static #_getAmPm() {
        return this.#_get('hours', 'Hours', 'Hours') >= 12 ? 'PM' : 'AM';
    }

    static #_getOrdinal(n) {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return s[(v - 20) % 10] || s[v] || s[0];
    }

    /**
     * Formats a given input date into a specified string format.
     *
     * This function decomposes the input date into various components such as year, month, day,
     * hours, minutes, and seconds, as well as textual representations like weekdays and month names.
     * These components are then substituted into the provided format string.
     *
     * Supported format tokens include:
     * - `Y`: Full year (e.g., 2023)
     * - `y`: Last two digits of the year (e.g., 23 for the year 2023)
     * - `m`: Two-digit month (e.g., 01 for January)
     * - `n`: Numeric month without leading zero (e.g., 1 for January)
     * - `F`: Full month name (e.g., January)
     * - `M`: Abbreviated month name (e.g., Jan)
     * - `d`: Day of the month with leading zero (e.g., 01)
     * - `j`: Day of the month without leading zero (e.g., 1)
     * - `D`: Abbreviated weekday name (e.g., Mon)
     * - `l`: Full weekday name (e.g., Monday)
     * - `w`: Numeric representation of the day of the week (0 for Sunday, 6 for Saturday)
     * - `N`: ISO-8601 numeric representation of the day of the week (1 for Monday, 7 for Sunday)
     * - `g`: 12-hour format of an hour without leading zero (e.g., 1 for 1 PM)
     * - `G`: 24-hour format of an hour without leading zero (e.g., 13 for 1 PM)
     * - `h`: 12-hour format of an hour with leading zero (e.g., 01 for 1 PM)
     * - `H`: 24-hour format of an hour with leading zero (e.g., 13 for 1 PM)
     * - `i`: Minutes with leading zero (e.g., 09)
     * - `s`: Seconds with leading zero (e.g., 09)
     * - `a`: Lowercase ante meridiem and post meridiem (`am` or `pm`)
     * - `A`: Uppercase ante meridiem and post meridiem (`AM` or `PM`)
     * - `S`: English ordinal suffix for the day of the month (e.g., `st`, `nd`, `rd`, `th`)
     * - `z`: Day of the year (starting from 0, e.g., 0 for January 1)
     * - `t`: Number of days in the month (e.g., 31)
     * - `L`: Leap year flag (1 if a leap year, 0 otherwise)
     *
     * Escaping special characters in the input format string can be done with a backslash (`\`).
     *
     * @param {string|Date} inputdate - The input date to be formatted. This can be a string parseable by `Date`, or a `Date` object.
     * @param {string} [formatStr="Y-m-d H:i:s"] - The format string defining the desired output format.
     * @returns {string} The formatted date string.
     */
    static format = (inputdate, formatStr = "Y-m-d H:i:s") => {
        this.date = new Date(inputdate);
        const Y = this.#_get('year', 'FullYear', 'FullYear');
        const y = String(Y).slice(-2);
        const m = this.#_pad(this.#_get('month', 'Month', 'Month') + 1);
        const n = this.#_get('month', 'Month', 'Month') + 1;
        const F = this.#_getMonthName(false);
        const M = this.#_getMonthName(true);
        const d = this.#_pad(this.#_get('date', 'Date', 'Date'));
        const j = this.#_get('date', 'Date', 'Date');
        const D = this.#_getWeekdayName(true);
        const l = this.#_getWeekdayName(false);
        const w = this.#_get('day', 'Day', 'Day');
        const N = w === 0 ? 7 : w;
        const g = this.#_get('hours', 'Hours', 'Hours') % 12 || 12;
        const G = this.#_get('hours', 'Hours', 'Hours');
        const h = this.#_pad(g);
        const H = this.#_pad(G);
        const i = this.#_pad(this.#_get('minutes', 'Minutes', 'Minutes'));
        const s = this.#_pad(this.#_get('seconds', 'Seconds', 'Seconds'));
        const a = this.#_getAmPm().toLowerCase();
        const A = this.#_getAmPm();
        const S = this.#_getOrdinal(j);
        const z = Math.floor((this.date - new Date(Y, 0, 1)) / 86400000);
        const t = new Date(Y, n, 0).getDate();
        const L = (Y % 4 === 0 && Y % 100 !== 0) || Y % 400 === 0 ? 1 : 0;

        const replacements = {
            Y, y, m, n, F, M, d, j, D, l, w, N,
            g, G, h, H, i, s, a, A, S, z, t, L
        };

        return formatStr.replace(/\\?([a-zA-Z])/g, (match, token) =>
            match.startsWith('\\') ? token : (replacements[token] ?? token)
        );
    }

    /**
     * Calculates the difference in time from a given number of milliseconds,
     * and formats the result into days, hours, minutes, and seconds.
     *
     * @param {number} milliseconds - The time difference in milliseconds to be converted.
     * @param {boolean} [eachNumberSeparated=false] - Optional flag indicating whether
     *        each number should be returned as an array of individual digits.
     *
     * @returns {Object} An object representing the time difference, with the keys:
     *     - `d`: Days (formatted as either a string or an array of two digits based on the flag).
     *     - `h`: Hours (formatted as either a string or an array of two digits based on the flag).
     *     - `m`: Minutes (formatted as either a string or an array of two digits based on the flag).
     *     - `s`: Seconds (formatted as either a string or an array of two digits based on the flag).
     */
    static timeDiff = (milliseconds, eachNumberSeparated = false) => {
        const days = Math.floor(milliseconds / 86400000); // 1000 * 60 * 60 * 24
        const hours = Math.floor((milliseconds % 86400000) / 3600000);
        const minutes = Math.floor((milliseconds % 3600000) / 60000);
        const seconds = Math.floor((milliseconds % 60000) / 1000);

        const pad = n => String(n).padStart(2, '0');

        if (eachNumberSeparated) {
            return {
                d: [pad(days)[0], pad(days)[1]],
                h: [pad(hours)[0], pad(hours)[1]],
                m: [pad(minutes)[0], pad(minutes)[1]],
                s: [pad(seconds)[0], pad(seconds)[1]]
            };
        }
        return {
            d: pad(days),
            h: pad(hours),
            m: pad(minutes),
            s: pad(seconds)
        };
    }


}

registerGlobal("Datetime", Datetime);
